/**
 * SalonID Premium Benefits - Enhanced Controller
 * Desktop: Reversible animations
 * Mobile: Native scroll with snap
 */
(function() {
  'use strict';

  class PremiumBenefitsAnimator {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector('.salonid-premium-benefits__grid');
      this.cards = Array.from(section.querySelectorAll('[data-benefit-card]'));
      this.counters = Array.from(section.querySelectorAll('[data-counter]'));
      this.countersAnimated = new Set();
      
      this.isMobile = window.innerWidth < 768;
      
      this.intersectionObserver = null;
      this.counterObserver = null;
      
      this.init();
    }

    init() {
      this.applyCardIndices();
      this.setupGradients();
      this.setupIntersectionObserver();
      
      // Only setup hover effects on desktop
      if (!this.isMobile) {
        this.setupHoverEffects();
      }
      
      // Handle window resize
      this.handleResize = this.debounce(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 768;
        
        if (wasMobile !== this.isMobile) {
          // Reinitialize on mobile/desktop switch
          this.destroy();
          this.init();
        }
      }, 250);
      
      window.addEventListener('resize', this.handleResize);
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    applyCardIndices() {
      this.cards.forEach((card, index) => {
        card.style.setProperty('--card-index', index);
      });
    }

    setupGradients() {
      this.cards.forEach(card => {
        const gradient = card.dataset.gradient;
        if (!gradient) return;
        
        const gradientElement = card.querySelector('.salonid-premium-benefits__card-gradient');
        if (gradientElement) {
          gradientElement.style.background = gradient;
        }
      });
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: show all elements immediately
        this.cards.forEach(card => card.classList.add('animate-in'));
        this.counters.forEach(counter => this.animateCounter(counter));
        return;
      }

      // Desktop: Reversible card animations
      // Mobile: Show immediately
      if (!this.isMobile) {
        const options = {
          threshold: [0, 0.2, 0.5, 1],
          rootMargin: '-50px 0px'
        };

        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            const target = entry.target;
            
            if (entry.isIntersecting) {
              target.classList.remove('animate-out');
              target.classList.add('animate-in');
            } else {
              if (target.classList.contains('animate-in')) {
                target.classList.remove('animate-in');
                target.classList.add('animate-out');
              }
            }
          });
        }, options);

        this.cards.forEach(card => this.intersectionObserver.observe(card));
      } else {
        // Mobile: just show all cards
        this.cards.forEach(card => {
          card.classList.add('animate-in');
          card.style.opacity = '1';
          card.style.transform = 'none';
        });
      }

      // Counter observer - works on both mobile and desktop
      this.counterObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.countersAnimated.has(entry.target)) {
            this.animateCounter(entry.target);
            this.countersAnimated.add(entry.target);
          }
        });
      }, { 
        threshold: 0.5,
        rootMargin: '0px'
      });

      this.counters.forEach(counter => this.counterObserver.observe(counter));
    }

    animateCounter(element) {
      const target = parseInt(element.dataset.target);
      if (isNaN(target)) return;

      let start = 0;
      const duration = 2000;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease out quart
        const eased = 1 - Math.pow(1 - progress, 4);
        const current = Math.floor(eased * target);
        
        element.textContent = current;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          element.textContent = target;
        }
      };

      requestAnimationFrame(animate);
    }

    setupHoverEffects() {
      this.cards.forEach(card => {
        const spotlight = card.querySelector('.salonid-premium-benefits__card-spotlight');
        if (!spotlight) return;

        card.addEventListener('mousemove', (e) => {
          const rect = card.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          
          spotlight.style.background = `radial-gradient(600px circle at ${x}% ${y}%, rgba(185, 167, 255, 0.15), transparent 40%)`;
        });
      });
    }

    destroy() {
      // Clean up event listener
      window.removeEventListener('resize', this.handleResize);
      
      // Clean up observers
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      if (this.counterObserver) {
        this.counterObserver.disconnect();
        this.counterObserver = null;
      }
    }
  }

  let animators = [];

  function initPremiumBenefits() {
    // Clean up existing animators
    animators.forEach(animator => animator.destroy());
    animators = [];

    const sections = document.querySelectorAll('.salonid-premium-benefits');
    
    if (sections.length === 0) {
      return;
    }

    sections.forEach(section => {
      const animator = new PremiumBenefitsAnimator(section);
      animators.push(animator);
    });
    
    console.log('✅ SalonID Premium Benefits initialized -', animators.length, 'sections');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPremiumBenefits);
  } else {
    initPremiumBenefits();
  }

  // Re-initialize on Shopify theme editor updates
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(initPremiumBenefits, 100);
    });
    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(initPremiumBenefits, 100);
    });
  }

  // Cleanup on unload
  window.addEventListener('beforeunload', () => {
    animators.forEach(animator => animator.destroy());
  });

  // Export for potential external use
  window.SalonIDPremiumBenefits = PremiumBenefitsAnimator;
})();